/**
 * Subscription Tier Access Helper
 * 
 * Tier hierarchy:
 * - none (free): permanent access to basic features only
 * - monthly: unlocks tier1 features
 * - quarterly: unlocks tier1 + tier2 features
 * - annual: unlocks tier1 + tier2 + tier3 features
 * 
 * Free tier features (permanent, no time limit):
 * - Today's Check-in
 * - Mood Tracker
 * - Energy Tracker
 * - Journal (free-form)
 * - Gratitude journal
 * - Two soundscapes: Rain, Ocean Waves
 * - One meditation: Breath Reset (3 minutes)
 * 
 * Feature gating:
 * - tier1 (monthly+): Community, Tools/Vault, Mini-quests, Daily signals, Reframe tool, Streaks, Full soundscapes, Full meditations
 * - tier2 (quarterly+): Badges, Weekly Reflections
 * - tier3 (annual): Quests (courses), Pathfinder Prize
 */

// Tier levels for comparison
const TIER_LEVELS = {
  none: 0,
  monthly: 1,
  quarterly: 2,
  annual: 3
};

// Features available to free tier (subscription_type: none)
export const FREE_TIER_FEATURES = {
  checkIn: true,
  moodTracker: true,
  energyTracker: true,
  journal: true,
  gratitude: true,
  // Limited soundscapes for free tier
  freeSoundscapes: ['rain', 'ocean-waves'],
  // Limited meditations for free tier
  freeMeditations: ['breath-reset']
};

// Feature to required tier mapping
const FEATURE_TIERS = {
  // Free tier features (available to all including subscription_type: none)
  checkIn: 'free',
  journal: 'free',
  gratitude: 'free',
  moodTracker: 'free',
  energyTracker: 'free',
  
  // Tier 1 - Monthly and above (paid features)
  community: 'monthly',
  tools: 'monthly',
  vault: 'monthly',
  miniQuests: 'monthly',
  dailySignals: 'monthly',
  reframe: 'monthly',
  streaks: 'monthly',
  fullSoundscapes: 'monthly',
  fullMeditations: 'monthly',
  pathfinder: 'monthly',      // AI Companion - available to all paid tiers
  
  // Tier 2 - Quarterly and above
  soundscapes: 'quarterly',
  meditations: 'quarterly',
  badges: 'quarterly',
  weeklyReflections: 'quarterly',
  
  // Tier 3 - Annual only
  quests: 'annual',
  pathfinderPrize: 'annual'   // The prize/reward, not the feature itself
};

/**
 * Check if user has access to a feature based on subscription type
 * @param {string} userSubscriptionType - User's current subscription: 'none', 'monthly', 'quarterly', 'annual'
 * @param {string} requiredTier - Required tier: 'free', 'monthly', 'quarterly', 'annual'
 * @param {object} user - Optional user object to check for admin role
 * @returns {boolean} - true if user has access
 */
export function hasAccess(userSubscriptionType, requiredTier, user = null) {
  // Admin users bypass all subscription gating
  if (user?.role === 'admin') return true;
  
  // Free tier features are always accessible
  if (requiredTier === 'free') return true;
  
  const userLevel = TIER_LEVELS[userSubscriptionType] || 0;
  const requiredLevel = TIER_LEVELS[requiredTier] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Check if user has access to a specific feature
 * @param {string} userSubscriptionType - User's current subscription
 * @param {string} feature - Feature name from FEATURE_TIERS
 * @param {object} user - Optional user object to check for admin role
 * @returns {boolean}
 */
export function hasFeatureAccess(userSubscriptionType, feature, user = null) {
  // Admin users bypass all subscription gating
  if (user?.role === 'admin') return true;
  
  const requiredTier = FEATURE_TIERS[feature];
  if (!requiredTier) return true; // Unknown features are open
  return hasAccess(userSubscriptionType, requiredTier, user);
}

/**
 * Check if a soundscape is available for free tier
 * @param {string} soundscapeId - Soundscape identifier
 * @returns {boolean}
 */
export function isFreeSoundscape(soundscapeId) {
  return FREE_TIER_FEATURES.freeSoundscapes.includes(soundscapeId?.toLowerCase());
}

/**
 * Check if a meditation is available for free tier
 * @param {string} meditationId - Meditation identifier
 * @returns {boolean}
 */
export function isFreeMeditation(meditationId) {
  return FREE_TIER_FEATURES.freeMeditations.includes(meditationId?.toLowerCase());
}

/**
 * Get the required tier for a feature
 * @param {string} feature - Feature name
 * @returns {string} - Required tier name
 */
export function getRequiredTier(feature) {
  return FEATURE_TIERS[feature] || 'monthly';
}

/**
 * Get human-readable tier name
 * @param {string} tier - Tier key
 * @returns {string}
 */
export function getTierDisplayName(tier) {
  const names = {
    none: 'Free Plan',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annual: 'Annual'
  };
  return names[tier] || tier;
}

/**
 * Get upgrade message based on required tier
 * @param {string} requiredTier - Required tier
 * @param {boolean} isFreeUser - Whether user is on free tier
 * @returns {string}
 */
export function getUpgradeMessage(requiredTier, isFreeUser = false) {
  if (isFreeUser) {
    return 'Upgrade to unlock';
  }
  const messages = {
    monthly: 'Subscribe to unlock this feature',
    quarterly: 'Quarterly plan or above required',
    annual: 'Annual plan required'
  };
  return messages[requiredTier] || 'Upgrade to unlock';
}

// Membership upgrade URL
export const MEMBERSHIP_URL = 'https://www.truenorth-zone.com/pages/compass-membership';

export default {
  hasAccess,
  hasFeatureAccess,
  isFreeSoundscape,
  isFreeMeditation,
  getRequiredTier,
  getTierDisplayName,
  getUpgradeMessage,
  MEMBERSHIP_URL,
  TIER_LEVELS,
  FEATURE_TIERS,
  FREE_TIER_FEATURES
};
